/**
 * Sets window.__previsAppPainted once the browser has composited the app's
 * first frame (double requestAnimationFrame after mount).
 *
 * Unity's WebViewBlankScreenGuard probes this flag before showing its
 * "Display Error" dialog: pixel sampling cannot distinguish a legitimately
 * sparse dark page (e.g. the Projects home with two tiles on #0d0f14) from a
 * blank webview, so this flag is the page's own testimony that it rendered.
 * True failures (dead WKWebView process, failed load) never execute this JS,
 * so the guard's real safety net is preserved.
 *
 * Must stay dependency-free (no capabilities/bridge imports) — it runs on
 * every page in every mode, before initBridge.
 */
/** The id index.html gives its pre-bundle holding spinner. Exported so the source-scanning test can
 *  read ONE spelling off this module and look for it in the HTML, rather than restating it. */
export const HOLDING_SPINNER_ID = 'previs-holding';

/**
 * 20260921 (owner) — take index.html's holding spinner down.
 *
 * Called from the same double-rAF that raises the flag above, i.e. after the browser has
 * composited the app's first frame — so the swap is one cut from the ring to the app, with no
 * frame belonging to neither. Deliberately NOT earlier: React's first commit is not a paint, and
 * removing on mount would put a blank frame back exactly where this exists to remove one.
 *
 * The attribute goes with the node. Removing the node alone would be enough today, but the
 * attribute is what the stylesheet gates on, and leaving it set would re-show any future copy of
 * that markup the instant one appeared.
 *
 * Silent and total: a document that never had the spinner (standalone, online review, the debug
 * harness, jsdom) finds nothing and does nothing.
 */
function removeHoldingSpinner(): void {
  if (typeof document === 'undefined') return;
  try {
    document.getElementById(HOLDING_SPINNER_ID)?.remove();
    document.documentElement?.removeAttribute('data-previs-holding');
  } catch { /* detached/teardown document — nothing to clean up */ }
}

export function installPaintedFlag(): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      (window as unknown as Record<string, unknown>).__previsAppPainted = true;
      removeHoldingSpinner();
    });
  });
}
